import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useApp } from './AppContext';
import { TIERS, getFeatureAccess, getLimit, isDevUser, getTierConfig, hasFullAccess } from '../config/tiers';
import { devError, devLog } from '../utils/debugLogger';
import { 
  initializeRevenueCat, 
  getOfferings, 
  purchasePackage, 
  restorePurchases,
  hasProEntitlement,
  identifyUser,
  logoutUser,
  isRevenueCatAvailable,
  isNativePlatform,
  presentPaywall,
  presentPaywallIfNeeded,
  presentCustomerCenter,
  syncToFirestore,
  setUserAttributes,
  addCustomerInfoListener,
  getProSubscriptionDetails,
  PRO_ENTITLEMENT_ID,
} from '../services/revenueCat';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, authLoading } = useApp();
  const [tier, setTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [revenueCatInitialized, setRevenueCatInitialized] = useState(false);
  const [iOSOfferings, setIOSOfferings] = useState(null);

  // Initialize RevenueCat for native iOS/Android
  useEffect(() => {
    const initRC = async () => {
      if (isNativePlatform() && user?.userId) {
        const success = await initializeRevenueCat(user.userId);
        setRevenueCatInitialized(success);
        
        if (success) {
          // Fetch available subscription offerings
          const offerings = await getOfferings();
          setIOSOfferings(offerings);
          devLog('[Subscription] RevenueCat offerings loaded:', offerings);
          
          // Check if user has active subscription via RevenueCat
          const hasPro = await hasProEntitlement();
          if (hasPro) {
            devLog('[Subscription] User has active RevenueCat pro entitlement');
            // Sync to Firestore if not already pro
            if (tier !== 'pro' && tier !== 'dev' && tier !== 'friend') {
              await setDoc(doc(db, 'users', user.userId), {
                tier: 'pro',
                subscriptionSource: 'revenuecat',
              }, { merge: true });
            }
          }
        }
      }
    };
    
    if (!authLoading && user) {
      initRC();
    }
  }, [user, authLoading, tier]);

  useEffect(() => {
    // Get the user ID - AppContext uses userId, not uid
    const userId = user?.userId;
    
    // Wait for auth to finish loading before making any decisions
    if (authLoading) {
      setLoading(true);
      return;
    }
    
    if (!user || !userId) {
      setTier('free');
      setSubscriptionData(null);
      setLoading(false);
      return;
    }

    // Check if user is a dev first
    const isDev = isDevUser(userId);
    
    if (isDev) {
      setTier('dev');
      setSubscriptionData({ tier: 'dev', isDev: true });
      setLoading(false);
      
      // Also update Firestore to reflect dev tier
      setDoc(doc(db, 'users', userId), { 
        tier: 'dev',
        email: user.email?.toLowerCase() || ''
      }, { merge: true }).catch(err => {
        devError('[Subscription] Error updating dev tier in Firestore:', err);
      });
      
      return;
    }

    // Listen to user's subscription document
    const unsubscribe = onSnapshot(
      doc(db, 'users', userId),
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Backfill email for existing users if missing
          if (!data.email && user.email) {
            await setDoc(doc(db, 'users', userId), { 
              email: user.email.toLowerCase() 
            }, { merge: true });
          }
          
          // Check subscription expiration
          let currentTier = data.tier || 'free';
          
          if (data.subscriptionEnd) {
            const endDate = data.subscriptionEnd.toDate ? 
              data.subscriptionEnd.toDate() : 
              new Date(data.subscriptionEnd);
            
            if (endDate < new Date()) {
              // Subscription expired, revert to free
              currentTier = 'free';
              // Update the document
              await setDoc(doc(db, 'users', userId), {
                ...data,
                tier: 'free',
                subscriptionExpired: true,
              }, { merge: true });
            }
          }
          
          setTier(currentTier);
          setSubscriptionData(data);
        } else {
          // Document doesn't exist - this can happen during account deletion
          // Don't try to create a new document, just set default state
          // The user is likely being deleted or has been deleted
          setTier('free');
          setSubscriptionData(null);
        }
        setLoading(false);
      },
      (error) => {
        devError('Error listening to subscription:', error);
        setTier('free');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  /**
   * Check if user has access to a specific feature
   */
  const hasFeature = (feature) => {
    return getFeatureAccess(tier, feature);
  };

  /**
   * Get the limit for a specific item type
   */
  const getItemLimit = (limitName) => {
    return getLimit(tier, limitName);
  };

  /**
   * Check if user is on pro tier or higher (pro, friend, or dev)
   */
  const isPro = hasFullAccess(tier);

  /**
   * Check if user is a dev/admin
   */
  const isDev = tier === 'dev';

  /**
   * Check if user is a friend of mind.
   */
  const isFriend = tier === 'friend';

  /**
   * Check if user can add more of an item type
   */
  const canAddMore = (itemType, currentCount) => {
    const limit = getItemLimit(itemType);
    return currentCount < limit;
  };

  /**
   * Get remaining quota for an item type
   */
  const getRemainingQuota = (itemType, currentCount) => {
    const limit = getItemLimit(itemType);
    if (limit === Infinity) return Infinity;
    return Math.max(0, limit - currentCount);
  };

  /**
   * Manually update tier (for admin use or after payment)
   */
  const updateTier = async (newTier, subscriptionInfo = {}) => {
    if (!user) return;
    
    try {
      await setDoc(doc(db, 'users', user.userId), {
        tier: newTier,
        ...subscriptionInfo,
        updatedAt: new Date(),
      }, { merge: true });
    } catch (error) {
      devError('Error updating tier:', error);
      throw error;
    }
  };

  /**
   * Purchase subscription via RevenueCat (iOS/Android only)
   * @param {Object} packageToPurchase - Package from iOSOfferings
   */
  const purchaseIOSSubscription = useCallback(async (packageToPurchase) => {
    if (!isNativePlatform() || !revenueCatInitialized) {
      throw new Error('iOS purchases only available in native app');
    }

    try {
      const result = await purchasePackage(packageToPurchase);
      
      if (result.success && result.isPro) {
        // Update Firestore with pro tier
        await setDoc(doc(db, 'users', user.userId), {
          tier: 'pro',
          subscriptionSource: 'revenuecat',
          revenueCatId: result.customerInfo?.originalAppUserId,
          updatedAt: new Date(),
        }, { merge: true });
        
        devLog('[Subscription] iOS purchase successful, tier updated to pro');
      }
      
      return result;
    } catch (error) {
      devError('[Subscription] iOS purchase error:', error);
      throw error;
    }
  }, [revenueCatInitialized, user]);

  /**
   * Restore iOS purchases (e.g., after reinstall)
   */
  const restoreIOSPurchases = useCallback(async () => {
    if (!isNativePlatform() || !revenueCatInitialized) {
      throw new Error('iOS restore only available in native app');
    }

    try {
      const result = await restorePurchases();
      
      if (result.success && result.isPro) {
        await setDoc(doc(db, 'users', user.userId), {
          tier: 'pro',
          subscriptionSource: 'revenuecat',
          updatedAt: new Date(),
        }, { merge: true });
        
        devLog('[Subscription] iOS restore successful, tier updated to pro');
      }
      
      return result;
    } catch (error) {
      devError('[Subscription] iOS restore error:', error);
      throw error;
    }
  }, [revenueCatInitialized, user]);

  /**
   * Show the RevenueCat Paywall (native only)
   * Uses the paywall configured in RevenueCat Dashboard
   */
  const showPaywall = useCallback(async () => {
    if (!isNativePlatform()) {
      devLog('[Subscription] Paywall only available on native platforms');
      return { success: false, error: 'Not a native platform' };
    }

    try {
      const result = await presentPaywall();
      
      // If purchase was successful, sync to Firestore
      if (result.success && (result.purchased || result.restored)) {
        await syncToFirestore(user?.userId, db);
        devLog('[Subscription] Paywall purchase/restore successful');
      }
      
      return result;
    } catch (error) {
      devError('[Subscription] Paywall error:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  /**
   * Show paywall only if user doesn't have pro entitlement
   */
  const showPaywallIfNeeded = useCallback(async () => {
    if (!isNativePlatform()) {
      return { success: false, error: 'Not a native platform' };
    }

    try {
      const result = await presentPaywallIfNeeded();
      
      if (result.success && (result.purchased || result.restored)) {
        await syncToFirestore(user?.userId, db);
      }
      
      return result;
    } catch (error) {
      devError('[Subscription] Paywall if needed error:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  /**
   * Show the Customer Center for subscription management
   */
  const showCustomerCenter = useCallback(async () => {
    if (!isNativePlatform()) {
      devLog('[Subscription] Customer Center only available on native platforms');
      return;
    }

    try {
      await presentCustomerCenter();
    } catch (error) {
      devError('[Subscription] Customer Center error:', error);
      throw error;
    }
  }, []);

  /**
   * Get subscription details for current user
   */
  const getSubscriptionDetails = useCallback(async () => {
    if (!isNativePlatform()) {
      return subscriptionData;
    }
    
    return await getProSubscriptionDetails();
  }, [subscriptionData]);

  const value = {
    // State
    tier,
    loading,
    subscriptionData,
    tierConfig: getTierConfig(tier),
    
    // Checks
    hasFeature,
    getItemLimit,
    isPro,
    isDev,
    isFriend,
    canAddMore,
    getRemainingQuota,
    
    // Actions
    updateTier,
    
    // iOS In-App Purchases (RevenueCat)
    isNativeApp: isNativePlatform(),
    iOSOfferings,
    purchaseIOSSubscription,
    restoreIOSPurchases,
    
    // RevenueCat Paywalls & Customer Center
    showPaywall,           // Present the RevenueCat Paywall UI
    showPaywallIfNeeded,   // Show paywall only if user lacks pro entitlement
    showCustomerCenter,    // Show subscription management UI
    getSubscriptionDetails, // Get current subscription details
    
    // Constants
    TIERS,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
